import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useAuth } from '../../contexts/AuthContext';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface ClassSizeData {
  [className: string]: number;
}

const ClassSizeChart: React.FC = () => {
  const { token } = useAuth();
  const [data, setData] = useState<ClassSizeData>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchClassSizeData();
  }, []);

  const fetchClassSizeData = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/charts/class-sizes', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch class size data: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        setData(result.data);
        setError(null);
      } else {
        throw new Error(result.error || 'Failed to fetch data');
      }
    } catch (err) {
      console.error('Error fetching class size data:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const chartData = {
    labels: Object.keys(data),
    datasets: [
      {
        label: 'Number of Students',
        data: Object.values(data),
        backgroundColor: 'rgba(54, 162, 235, 0.8)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 2,
        borderRadius: 8,
        borderSkipped: false,
        maxBarThickness: 60,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: 'Class Size Distribution',
        font: {
          size: 16,
          weight: 'bold' as const,
        },
        color: '#2c3e50',
        padding: {
          bottom: 20
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: '#ddd',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          label: function(context: any) {
            const studentCount = context.raw;
            const studentText = studentCount === 1 ? 'student' : 'students';
            return `${studentCount} ${studentText}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          color: '#666',
          font: {
            size: 12,
          }
        },
        grid: {
          color: '#f0f0f0',
          drawBorder: false,
        },
        title: {
          display: true,
          text: 'Number of Students',
          color: '#666',
          font: {
            size: 14,
            weight: 'bold' as const,
          }
        }
      },
      x: {
        ticks: {
          color: '#666',
          font: {
            size: 11,
          },
          maxRotation: 45,
          minRotation: 0,
        },
        grid: {
          display: false,
          drawBorder: false,
        },
        title: {
          display: true,
          text: 'Class Name',
          color: '#666',
          font: {
            size: 14,
            weight: 'bold' as const,
          }
        }
      },
    },
    animation: {
      duration: 2000,
      easing: 'easeInOutCubic' as const,
      delay: (context: any) => context.dataIndex * 150,
    },
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
  };

  if (loading) {
    return (
      <div className="chart-loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
        <p>Loading class size data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chart-error">
        <p>Error loading chart: {error}</p>
        <button onClick={fetchClassSizeData} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  if (Object.keys(data).length === 0) {
    return (
      <div className="chart-empty">
        <p>No class data available</p>
        <p className="chart-empty-detail">Create some classes to see size distribution</p>
      </div>
    );
  }

  return (
    <div className="chart-container">
      <div className="chart-wrapper">
        <Bar data={chartData} options={options} />
      </div>
    </div>
  );
};

export default ClassSizeChart;
